import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import Navbar from '@/components/feature/Navbar';

interface BlogPostRow {
  id: number;
  title: string;
  slug: string;
  status: string;
  author: string;
  published_at: string | null;
  updated_at: string;
  created_at: string;
  tags: string[] | null;
}

export default function AdminDashboard() {
  const { user, isAdmin, signOut } = useAuth();
  const [posts, setPosts] = useState<BlogPostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('blog_posts')
        .select('id, title, slug, status, author, published_at, updated_at, created_at, tags')
        .order('updated_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (search.trim()) {
        query = query.or(`title.ilike.%${search}%,excerpt.ilike.%${search}%`);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setPosts((data as BlogPostRow[]) || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleDelete = async (id: number) => {
    setDeleting(true);
    try {
      const { error: deleteError } = await supabase.from('blog_posts').delete().eq('id', id);
      if (deleteError) throw deleteError;
      setPosts((prev) => prev.filter((p) => p.id !== id));
      setDeleteConfirm(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete post');
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = async (post: BlogPostRow) => {
    try {
      const { error: dupError } = await supabase
        .from('blog_posts')
        .insert({
          title: `${post.title} (Copy)`,
          slug: `${post.slug}-copy-${Date.now()}`,
          excerpt: '',
          content: '',
          author: post.author,
          status: 'draft',
          tags: post.tags || [],
        })
        .select('id')
        .single();

      if (dupError) throw dupError;
      fetchPosts();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to duplicate post');
    }
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      published: 'bg-green-100 text-green-700',
      draft: 'bg-foreground-100 text-foreground-600',
      scheduled: 'bg-accent-100 text-accent-700',
      archived: 'bg-secondary-200 text-secondary-700',
    };
    return `px-2 py-0.5 rounded-full text-[10px] font-body font-medium uppercase tracking-wider ${map[status] || ''}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <div className="flex-1 py-8 md:py-12">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-foreground-900 text-2xl md:text-3xl">Blog Posts</h1>
              <p className="text-foreground-500 font-body text-sm mt-1">
                {(user?.email || isAdmin) && <span>Signed in as {user?.email || 'Admin'}</span>}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/posts/new"
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-4 py-2.5 rounded-md text-sm font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-add-line text-base" />New Post
              </Link>
              <Link to="/" className="text-foreground-500 font-body text-sm hover:text-foreground-700 transition-colors whitespace-nowrap">
                <i className="ri-external-link-line mr-1" />View Website
              </Link>
              <button
                onClick={() => signOut()}
                className="text-foreground-500 font-body text-sm hover:text-foreground-700 transition-colors whitespace-nowrap"
              >
                Sign Out
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
            <div className="relative flex-1 max-w-[350px]">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search posts..."
                className="w-full border border-background-300 rounded-md pl-9 pr-3 py-2 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-background-50"
              />
              <i className="ri-search-line text-foreground-400 absolute left-3 top-1/2 -translate-y-1/2 text-sm" />
            </div>
            <div className="flex items-center gap-1.5">
              {['all', 'published', 'draft', 'scheduled', 'archived'].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-body font-medium whitespace-nowrap transition-colors capitalize ${
                    statusFilter === s
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <button
              onClick={fetchPosts}
              className="text-foreground-500 hover:text-foreground-700 transition-colors"
              title="Refresh"
            >
              <i className="ri-refresh-line" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
              <p className="text-red-700 font-body text-xs">{error}</p>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
              <i className="ri-article-line text-foreground-300 text-4xl mb-3 block" />
              <p className="text-foreground-500 font-body text-sm">No posts found.</p>
              <Link
                to="/admin/posts/new"
                className="inline-flex items-center gap-2 mt-4 bg-primary-500 text-background-50 px-4 py-2 rounded-md text-sm font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-add-line" />Create Your First Post
              </Link>
            </div>
          ) : (
            <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      <th className="text-left px-4 py-3 text-foreground-600 font-body text-xs font-medium uppercase tracking-wider">Title</th>
                      <th className="text-left px-4 py-3 text-foreground-600 font-body text-xs font-medium uppercase tracking-wider whitespace-nowrap">Status</th>
                      <th className="text-left px-4 py-3 text-foreground-600 font-body text-xs font-medium uppercase tracking-wider whitespace-nowrap hidden md:table-cell">Updated</th>
                      <th className="text-right px-4 py-3 text-foreground-600 font-body text-xs font-medium uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((post) => (
                      <tr key={post.id} className="border-b border-background-100/70 hover:bg-background-100/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="max-w-[350px]">
                            <p className="text-foreground-900 font-body text-sm font-medium truncate">{post.title}</p>
                            <p className="text-foreground-400 font-body text-xs mt-0.5">/{post.slug}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={statusBadge(post.status)}>{post.status}</span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-foreground-500 font-body text-xs whitespace-nowrap">
                            {post.updated_at ? new Date(post.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Link
                              to={`/admin/posts/${post.id}/edit`}
                              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors"
                              title="Edit"
                            >
                              <i className="ri-edit-line text-sm" />
                            </Link>
                            <Link
                              to={`/blog/${post.slug}`}
                              target="_blank"
                              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors"
                              title="Preview"
                            >
                              <i className="ri-eye-line text-sm" />
                            </Link>
                            <button
                              onClick={() => handleDuplicate(post)}
                              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors"
                              title="Duplicate"
                            >
                              <i className="ri-file-copy-line text-sm" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(post.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-foreground-950/50 backdrop-blur-sm">
          <div className="bg-background-50 rounded-lg p-6 max-w-[400px] w-full mx-4">
            <h3 className="font-heading text-foreground-900 text-lg mb-2">Delete Post?</h3>
            <p className="text-foreground-600 font-body text-sm mb-6">
              This action cannot be undone. Are you sure you want to delete this post?
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 rounded-md text-sm font-body text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
                className="px-4 py-2 rounded-md text-sm font-body font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}